import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { addAutoAcceptRuc } from "@/services/invoiceService";
import { invoiceKeys, supplierKeys } from "@/lib/queryKeys";

interface Props {
  /** When set, the dialog is open and asks about this emisor. Null = closed. */
  prompt: { rucEmisor: string; legalName: string | null } | null;
  onClose: () => void;
}

/**
 * Modal shown immediately after a user accepts a received CFE from an
 * emisor they haven't allowlisted yet. "Sí, siempre" inserts the RUT
 * into auto_accept_emisor_ruc so future comprobantes from this emisor
 * are auto-accepted at ingest time (no user action needed).
 *
 * Loading state lives INSIDE the modal — the primary button swaps to
 * a spinner + "Activando…" copy, and both buttons + the close X are
 * disabled while the mutation is in flight (the backfill iterates
 * row-by-row, so a 10+ pendientes case takes a few seconds). The
 * toast only fires on the terminal result (success / error).
 */
export function AutoAcceptEmisorPromptDialog({ prompt, onClose }: Props): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const enableMutation = useMutation({
    mutationFn: ({ rucEmisor, legalName }: { rucEmisor: string; legalName: string | null }) =>
      addAutoAcceptRuc(rucEmisor, legalName),
    onSuccess: (res) => {
      // Tailor the toast to whether we backfilled existing pendientes.
      // First-time allowlisters with a list full of pending rows get
      // immediate proof their click cleared work; people with zero
      // pendientes get the plain "future ones" confirmation.
      if (res.backfilledCount > 0) {
        toast.success(
          t(
            "recibidas.autoAcceptPromptBackfilledToast",
            "Listo — aceptamos {{n}} comprobante(s) pendiente(s) y los próximos se aceptarán solos.",
            { n: res.backfilledCount },
          ),
        );
      } else {
        toast.success(
          t(
            "recibidas.autoAcceptPromptEnabledToast",
            "Listo — aceptaremos automáticamente futuros comprobantes de este emisor.",
          ),
        );
      }
      // Invalidate all relevant caches. The list key is built by
      // invoiceKeys.recibidas() = ["invoices", "recibidas"] with extra
      // params appended at the call site — invalidating by that prefix
      // catches every list variant. Earlier I was passing ["recibidas"]
      // (wrong prefix) so the page never refetched. Same for the
      // pending-ack-count which lives under that prefix too.
      void qc.invalidateQueries({ queryKey: ["auto-accept-rucs"] });
      void qc.invalidateQueries({ queryKey: invoiceKeys.recibidas() });
      void qc.invalidateQueries({ queryKey: ["recibidas", "pending-ack-count"] });
      void qc.invalidateQueries({ queryKey: supplierKeys.all });
      onClose();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Error");
    },
  });

  const open = prompt != null;
  const issuerName = prompt?.legalName?.trim() || prompt?.rucEmisor || "este emisor";
  const submitting = enableMutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Block dismissal while the mutation is in flight — the user
        // can't bail mid-backfill (the backend is already iterating
        // and the cache invalidation is queued behind the success).
        if (!o && !submitting) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("recibidas.autoAcceptPromptTitle", "¿Aceptar siempre los comprobantes de este emisor?")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "recibidas.autoAcceptPromptDesc",
              "Cada vez que recibas un comprobante de {{name}}, Fiscalo lo aceptará automáticamente sin pedirte confirmación. Podés desactivarlo desde la ficha del proveedor en cualquier momento.",
              { name: issuerName },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t("recibidas.autoAcceptPromptNo", "Ahora no")}
          </Button>
          <Button
            onClick={() => {
              if (!prompt) return;
              enableMutation.mutate(prompt);
            }}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("recibidas.autoAcceptPromptLoading", "Activando…")}
              </>
            ) : (
              t("recibidas.autoAcceptPromptYes", "Sí, aceptar siempre")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
